import React, { useEffect, useRef, useState } from "react";
import "./PowerEvolution.css";

const PowerEvolution = () => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    // 1. Capture the current value in a local variable
    const currentSection = sectionRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.15 },
    );

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      // 2. Use the captured variable for cleanup
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, [hasAnimated]);

  return (
    <section
      className={`power-evolution ${hasAnimated ? "run-animation" : ""}`}
      ref={sectionRef}
    >
      <div className="evolution-container">
        <div className="evolution-header">
          <div className="badge-tech dark-mode">The Zynventics Advantage</div>
          <h2>Out with the Bulky. In with the Smart.</h2>
          <p>
            Watch how we replace 4 square feet of messy wires and heavy
            lead-acid batteries with one sleek, integrated device.
          </p>
        </div>

        {/* The Animation Stage */}
        <div className="animation-stage">
          <div className="table-surface"></div>

          {/* THE OLD WAY: Bulky Setup */}
          <div className="setup-old">
            <div className="messy-wires">
              <svg viewBox="0 0 100 100" className="wire red">
                <path
                  d="M10,90 Q40,10 90,90"
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="4"
                />
              </svg>
              <svg viewBox="0 0 100 100" className="wire black">
                <path
                  d="M20,90 Q50,30 80,90"
                  fill="none"
                  stroke="#333"
                  strokeWidth="4"
                />
              </svg>
            </div>
            <div className="old-inverter-box">Standard Inverter</div>
            <div className="old-batteries">
              <div className="lead-acid-box">Lead-Acid Battery</div>
              <div className="lead-acid-box">Lead-Acid Battery</div>
            </div>
          </div>

          {/* THE NEW WAY: Zynvert Device */}
          <div className="setup-zynvert">
            <div className="zynvert-device">
              <div className="device-screen">
                <span className="status-dot"></span>
                ZYNVENTICS
              </div>
            </div>

            <div className="power-flow-container">
              <div className="flow-dc">
                <span className="flow-label dc-label">Internal Lithium DC</span>
                <svg width="150" height="20" viewBox="0 0 150 20">
                  <line
                    x1="0"
                    y1="10"
                    x2="150"
                    y2="10"
                    className="power-line dc-line"
                  />
                </svg>
              </div>

              <div className="flow-ac">
                <span className="flow-label ac-label">Pure Sine Wave AC</span>
                <svg width="200" height="40" viewBox="0 0 200 40">
                  <path
                    d="M0,20 Q25,0 50,20 T100,20 T150,20 T200,20"
                    className="power-line ac-wave"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Stats */}
        <div className="comparison-stats">
          <div className="stat-item fade-stat">
            <h4>-80%</h4>
            <p>Space Required</p>
          </div>
          <div className="stat-item fade-stat delay-1">
            <h4>0</h4>
            <p>External Wires</p>
          </div>
          <div className="stat-item fade-stat delay-2">
            <h4>100%</h4>
            <p>Integrated Lithium</p>
          </div>
        </div>

        {/* Core Promises Section */}
        <div className="core-promises">
          <div className="promise-box fade-stat delay-3">
            <div className="promise-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 18v-6a6 6 0 0 1 12 0v6" />
                <path d="M12 4v4" />
                <path d="M8 4h8" />
                <path d="M5 22h14a2 2 0 0 0 2-2v-2H3v2a2 2 0 0 0 2 2z" />
              </svg>
            </div>
            <h5>Easily Carriable</h5>
            <p>
              Lightweight, portable design for effortless placement anywhere in
              your home.
            </p>
          </div>

          <div className="promise-box fade-stat delay-4">
            <div className="promise-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </div>
            <h5>100% Safe</h5>
            <p>
              Equipped with active alarms, built-in fuses, and a premium Battery
              Management System.
            </p>
          </div>

          <div className="promise-box fade-stat delay-5">
            <div className="promise-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </div>
            <h5>Affordable</h5>
            <p>
              Premium lithium technology engineered to fit perfectly within a
              standard household budget.
            </p>
          </div>
        </div>

        <div className="promise-tagline fade-stat delay-6">
          "A Promise is a Promise. Reliable power, engineered for you."
        </div>
      </div>
    </section>
  );
};

export default PowerEvolution;
